/**
 * Iter XX.243 · Gully Pulse — public cricket profile page.
 * Route: /pulse/u/:userId
 * Shows name / role / bio / followers / following / moments count +
 * recent moments + a follow/unfollow button (hidden for self).
 * NEVER exposes private cricket-graph fields.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchPulseProfile, followUser, unfollowUser } from "../lib/pulse";

export default function PulseProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = async () => setProfile(await fetchPulseProfile(userId));
  useEffect(() => { reload(); }, [userId]);

  if (!profile) {
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-100 p-6">
        <div className="text-neutral-500 text-[13px]">Loading…</div>
      </div>
    );
  }

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    if (profile.is_following) await unfollowUser(userId);
    else await followUser(userId);
    await reload();
    setBusy(false);
  };

  return (
    <div data-testid="pulse-profile-page" className="min-h-screen bg-neutral-950 text-neutral-100">
      <div className="mx-auto max-w-2xl px-5 pt-6 pb-24">
        <button
          data-testid="pulse-profile-back"
          onClick={() => navigate(-1)}
          className="text-[13px] text-neutral-500 hover:text-neutral-300 mb-6"
        >
          ← Back
        </button>

        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-full bg-neutral-800 flex items-center justify-center text-neutral-300 text-2xl font-medium">
            {(profile.name || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1">
            <div data-testid="pulse-profile-name" className="text-2xl font-medium">{profile.name}</div>
            {profile.role && (
              <div data-testid="pulse-profile-role" className="text-[13px] text-neutral-500">{profile.role}</div>
            )}
          </div>
          {!profile.is_self && (
            <button
              data-testid="pulse-profile-follow"
              onClick={toggle}
              disabled={busy}
              className={
                "px-4 py-2 rounded-full text-[13px] font-medium transition-colors " +
                (profile.is_following
                  ? "bg-neutral-800 text-neutral-100 hover:bg-neutral-700"
                  : "bg-emerald-500 text-neutral-950 hover:bg-emerald-400")
              }
            >
              {profile.is_following ? "Following" : "Follow"}
            </button>
          )}
        </div>

        {profile.bio && (
          <p data-testid="pulse-profile-bio" className="text-[14px] text-neutral-300 mb-5">
            {profile.bio}
          </p>
        )}

        <div className="flex gap-6 text-[13px] text-neutral-400 mb-6">
          <div data-testid="pulse-profile-followers">
            <b className="text-neutral-100">{profile.followers}</b> Followers
          </div>
          <div data-testid="pulse-profile-following">
            <b className="text-neutral-100">{profile.following}</b> Following
          </div>
          <div data-testid="pulse-profile-moments">
            <b className="text-neutral-100">{profile.moments}</b> Moments
          </div>
        </div>

        {(profile.recent_moments || []).length === 0 && (
          <div className="text-neutral-500 text-[14px] py-6">
            No cricket moments shared yet.
          </div>
        )}
        {(profile.recent_moments || []).map((m) => (
          <div
            key={m.moment_id}
            data-testid="pulse-profile-moment"
            className="rounded-2xl border border-neutral-800 bg-neutral-900/70 p-4 mb-3"
          >
            <div className="text-[16px] text-neutral-100 mb-1">
              {m.kind === "support" ? "🤝" : m.kind === "practice" ? "🏏"
                : m.kind === "milestone" ? "💯" : "🔥"} {m.title}
            </div>
            {m.body && <div className="text-[13px] text-neutral-400">{m.body}</div>}
            <div className="text-[11px] text-neutral-500 mt-2">
              {new Date(m.created_at).toLocaleDateString()}
              {" · "}{m.celebrations_count || 0} celebration{(m.celebrations_count || 0) !== 1 ? "s" : ""}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
